import React, { useRef } from "react"
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import Typography from "aldoo-ra/Typography"
import Spinner from "aldoo-ra/Spinner"
import AppCard from "./app-card"
import { useAppUpdates } from "./admin-site-update-scan"

const DeploymentViewer = () => {
  const { appList, statuses: latestStatuses, refreshKey, initialDataLoaded, toolConnectionChecked, toolConnected } = useAppUpdates()

  // Track previous state to only log on changes
  const prevStateRef = useRef({ toolConnectionChecked: false, toolConnected: false, initialDataLoaded: false, hasApps: false })

  const getRepositoriesData = async () => {
    // If we haven't received initial data yet, return loading state
    if (!initialDataLoaded) {
      return { data: [], totalItems: 0, loading: true }
    }
    
    // If no app list after initial data loaded, return empty
    if (!appList) {
      return { data: [], totalItems: 0 }
    }

    const repos = appList.map((repo) => {
      const localStatus = latestStatuses?.get(repo.id) || {}
      const isLoading = !latestStatuses || !localStatus.gitState
      const { updateTask } = localStatus

      return {
        ...repo,
        ...localStatus, // Spread the full localStatus (includes id, name, type, description, gitState, deploymentState)
        updateTask,
        isLoading: isLoading,
      }
    })


    return {
      data: repos,
      totalItems: repos.length,
    }
  }

  const statusColorMap = {
    updated: "bg-admin_success",
    pending: "bg-admin_progress",
    deploying: "bg-admin_primary",
    updating: "bg-admin_link",
    running: "bg-admin_link",
    created: "bg-admin_neutral_2",
    completed: "bg-admin_success",
    failed: "bg-admin_error",
    not_initialized: "bg-admin_error",
    unknown: "bg-admin_neutral_2",
    loading: "bg-admin_link"
  }

  const tableItemRender = [
    {
      id: "status",
      headerClassName: "w-[250px]",
      render: (_, item) => (
        <AppCard item={item} statusColorMap={statusColorMap} />
      ),
    },
  ]

  return (
    <div className="p-6">
      {/* Tittle and Buttons */}
      <div className="w-full flex items-center justify-between mb-6">
        <Typography
          component="h1"
          variant="admin_h3"
          className="ml-4"
        >
          Deployment Manager
        </Typography>

        <Button
          variant="admin_primary"
          className={`flex items-center space-x-2 w-40 justify-center border transition-all duration-600 cursor-default ${
            toolConnectionChecked
              ? (toolConnected
                  ? 'bg-admin_success/10 border-admin_success/30'
                  : 'bg-admin_error/10 border-admin_error/30')
              : 'bg-admin_progress/10 border-admin_progress/30'
          }`}
        >
          {/* The dot indicator */}
          <div className={`w-4 h-4 rounded-full flex-shrink-0 transition-all duration-600 ${
            toolConnectionChecked
              ? (toolConnected ? 'bg-admin_success' : 'bg-admin_error')
              : 'bg-admin_progress animate-pulse'
          }`}></div>
          {/* The label */}
          <span className={`transition-colors duration-600 ${
            toolConnectionChecked
              ? (toolConnected ? 'text-admin_success' : 'text-admin_error')
              : 'text-admin_progress'
          }`}>
            {!toolConnectionChecked ? 'Connecting...' :
             toolConnected ? 'Connected' : 'Disconnected'}
          </span>
        </Button>
      </div>

      {/* Show loading state while checking deployment tool connection */}
      {(() => {
        const hasApps = appList && appList.length > 0
        const currentState = { toolConnectionChecked, toolConnected, initialDataLoaded, hasApps }
        const prevState = prevStateRef.current
        const stateChanged =
          prevState.toolConnectionChecked !== currentState.toolConnectionChecked ||
          prevState.toolConnected !== currentState.toolConnected ||
          prevState.initialDataLoaded !== currentState.initialDataLoaded ||
          prevState.hasApps !== currentState.hasApps

        if (stateChanged) {
          prevStateRef.current = currentState
        }

        if (!toolConnectionChecked) {
          return (
            <div className="bg-admin_paper rounded-3xl p-12 flex flex-col items-center justify-center text-center">
              <Spinner variant="loading" className="mx-auto w-[50px] h-[50px] mb-4" />
              <Typography variant="admin_body" className="text-admin_text_secondary">
                Connecting...
              </Typography>
            </div>
          )
        } else if (!toolConnected) {
          return (
            <div className="bg-admin_paper rounded-3xl p-12 flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 rounded-full bg-admin_warning/10 flex items-center justify-center mb-4">
                <ExclamationTriangleIcon className="w-8 h-8 text-admin_warning" />
              </div>
              <Typography variant="admin_h4" className="text-admin_warning mb-2">
                Deployment Tool Disconnected
              </Typography>
              <Typography variant="admin_body" className="text-admin_text_secondary max-w-md">
                The deployment tool is not currently connected. It will reconnect automatically when available.
              </Typography>
            </div>
          )
        } else if (!initialDataLoaded) {
          return (
            <DataTable
              variant="admin_cards"
              apiMethod={getRepositoriesData}
              refreshKey={refreshKey}
              preferredHeight="74vh"
              itemHeight="60px"
              localUserPreferencesKey="admin.updates.deployments"
        styleOverrides={{
                container: "flex flex-col h-full bg-admin_paper rounded-3xl p-6 pt-0",
                header: "flex flex-row justify-left items-center",
              }}
              renders={tableItemRender}
              noData={
                <div className="flex flex-col items-center justify-center py-8">
                  <Spinner variant="loading" className="mx-auto w-[50px] h-[50px] mb-4" />
                  <Typography variant="admin_body">Loading apps...</Typography>
                </div>
              }
              loading={true}
            />
          )
        } else if (!hasApps) {
          return (
            <DataTable
              variant="admin_cards"
              apiMethod={getRepositoriesData}
              refreshKey={refreshKey}
              preferredHeight="74vh"
              itemHeight="60px"
              styleOverrides={{
                container: "flex flex-col h-full bg-admin_paper rounded-3xl p-6 pt-0",
                header: "flex flex-row justify-left items-center",
              }}
              renders={tableItemRender}
              noData={<Typography variant="admin_body">No repositories found</Typography>}
              loading={false}
            />
          )
        } else {
          return (
            <DataTable
              variant="admin_cards"
              apiMethod={getRepositoriesData}
              refreshKey={refreshKey}
              preferredHeight="74vh"
              itemHeight="60px"
              styleOverrides={{
                container: "flex flex-col h-full bg-admin_paper rounded-3xl p-6 pt-0",
                header: "flex flex-row justify-left items-center",
              }}
              renders={tableItemRender}
              noData={<Typography variant="admin_body">No repositories found</Typography>}
              loading={false}
            />
          )
        }
      })()}
    </div>
  )
}

export default DeploymentViewer